import { ButtonBase, Collapse } from '@mui/material';
import SvgArrowDown from 'assets/pricing/arrow-down.inline.svg';
import SvgArrowUp from 'assets/pricing/arrow-up.inline.svg';
import IconPlus from 'assets/pricing/circle-plus.inline.svg';
import ButtonIconLeft from 'components/material-ui/ButtonIconLeft';
import { PRICING_PLAN, PRICING_PLAN_OPTION_CATEGORY, PRICING_PLAN_OPTION_TYPE } from 'components/pages/pricing/base/enumsPricing';
import HelperBox from 'components/pages/pricing/items/HelperBox';
import { routes } from 'constants/routes';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import useLanguage from 'hooks/useLanguage';
import { searchParams } from 'hooks/useSearchParams';
import parse from 'html-react-parser';
import { usePersistState } from 'providers/PersistProvider';
import React, { useRef } from 'react';
import tw from 'twin.macro';
import { formatValueToEurope } from 'utils/helper';

export default function Plan({ isMobile, isOpen, plan, onOpen, onClose }) {
	const { option } = usePersistState();
	const { language } = useLanguage();
	const { t } = useTranslation();
	const refPlan = useRef();

	const isSilver = plan.name === PRICING_PLAN.SILVER;
	const isGold = plan.name === PRICING_PLAN.GOLD;
	const isPlatinum = plan.name === PRICING_PLAN.PLATINUM;

	const handleTogglePlan = event => {
		if (isOpen) {
			refPlan.current.scrollIntoView({ behavior: 'smooth' });
			setTimeout(() => {
				onClose(event);
			}, 300);
		} else {
			onOpen(event);
		}
	};

	if (!option?.durationInMonths) return null;

	const planCurrentOption = plan?.pricingSetting?.find(item => item?.planDuration?.duration === option.durationInMonths);
	const isInaccessible = !planCurrentOption?.price;

	return (
		<div className="h-full relative">
			<div ref={refPlan} className="absolute top-0 -mt-20"/>

			<div
				style={{ maxWidth: '24rem' }}
				css={[
					tw`flex flex-col justify-between sm:h-full sm:w-170 lg:w-200 xl:w-240 relative border-2 border-grey-E rounded-10 bg-light mx-auto`,
					isInaccessible && tw`bg-grey-F`
				]}
			>
				<div>
					{isSilver && <div style={{ marginTop: '-0.25rem' }} className="absolute ml-12 xl:ml-22 top-0 w-40 h-03 xl:h-04 bg-grey-C rounded-10"/>}
					{isGold && <div style={{ marginTop: '-0.25rem' }} className="absolute ml-12 xl:ml-22 top-0 w-30 h-03 xl:h-04 bg-accent-0 rounded-10"/>}
					{isPlatinum && <div style={{ marginTop: '-0.25rem' }} className="absolute ml-12 xl:ml-22 top-0 w-50 h-03 xl:h-04 bg-blue-1 rounded-10"/>}

					<div className="py-10 sm:py-18 px-12 sm:px-12 xl:px-22">
						<p className="text-14 font-bold mt-07 mb-10 sm:mb-05">{PRICING_PLAN[plan.name].toUpperCaseChar()}</p>
						<p className="leading-12 text-grey-7 h-120">{parse(plan?.description ?? '')}</p>

						<p
							css={[
								tw`text-12 text-grey-6 mt-20 sm:mt-80 xl:mt-40`,
								!isInaccessible && tw`line-through`
							]}
						>
							{planCurrentOption?.promoPrice ? formatValueToEurope(planCurrentOption?.price, planCurrentOption?.currency?.code) : '\xA0'}
						</p>
						<p
							css={[
								tw`text-15 font-bold my-09 xl:my-05`,
								!isInaccessible && tw`text-green-1`
							]}
						>
							{formatValueToEurope(planCurrentOption?.promoPrice || planCurrentOption?.price, planCurrentOption?.currency?.code, { emptyValue: t('pagePricing.plan.inaccessible') })}
						</p>

						<p className="leading-12 text-grey-7 h-50">{planCurrentOption?.priceDescription}</p>

						<ButtonIconLeft
							name={`choosePlan-1-${PRICING_PLAN[plan.name]}`}
							Icon={IconPlus}
							disabled={isInaccessible}
							to={`${routes[language].BASKET_PRODUCT_OPTIONS_SELECTION}?${searchParams.plan}=${PRICING_PLAN[plan.name]}`}
							tw="mx-auto mt-20 mb-30 sm:my-40" size="medium">
							{t('pagePricing.label.choosePlan')}
						</ButtonIconLeft>
					</div>

					<Collapse in={isOpen}>
						{plan?.overviewCategories?.map(overviewCategory => (
							<>
								<p className="pl-20 mx-10 mb-07 font-bold">{PRICING_PLAN_OPTION_CATEGORY[overviewCategory.category]}</p>
								<ul className="ml-20 mr-03 mb-20">
									{overviewCategory.overviewOptions?.map(overviewOption => (
										<li
											key={overviewOption.category}
											className={overviewOption.optionType === PRICING_PLAN_OPTION_TYPE.PAY_EXTRA ? (
												isInaccessible ? 'li-custom-disabled-emptymark' : 'li-custom-emptymark'
											) : (
												isInaccessible ? 'li-custom-disabledmark' : 'li-custom-checkmark'
											)}
										>
											{overviewOption.name}
										</li>
									))}
								</ul>
							</>
						))}
					</Collapse>
				</div>

				<div>
					{isOpen && (
						<ButtonIconLeft
							name={`choosePlan-2-${PRICING_PLAN[plan.name]}`}
							Icon={IconPlus}
							disabled={isInaccessible}
							to={`${routes[language].BASKET_PRODUCT_OPTIONS_SELECTION}?${searchParams.plan}=${PRICING_PLAN[plan.name]}`}
							tw="mx-auto mb-50 sm:mb-20 xl:mb-50" size="medium"
						>
							{t('pagePricing.label.choosePlan')}
						</ButtonIconLeft>
					)}
				</div>

				<div style={{ marginBottom: '-27px', marginLeft: '-27px' }} className="sm:hidden absolute bottom-0 left-50/100">
					<ButtonBase tw="rounded-full" onClick={handleTogglePlan}>
						{isOpen ? <SvgArrowUp/> : <SvgArrowDown/>}
					</ButtonBase>
				</div>
			</div>
			{isMobile && isOpen && <HelperBox/>}
		</div>
	);
}

Plan.defaultProps = {};
